import { useRef, useState } from 'react';
import { useLingui } from '@lingui/react/macro';
import { Download } from 'lucide-react';
import { cn } from '@worldcoo/ui/lib/utils';
import { Carousel, CarouselItem, carouselStep } from '@worldcoo/ui/components/carousel';
import { annualReportUrl, annualReports } from '@/lib/annual-reports';
import { useTranslation } from '@/lib/i18n';
import { DiamondGrid, diamondGlows } from '../primitives';
import { HighFive } from './high-five';

// Newest first; `annual-reports.ts`, shared with the transparency page, is oldest-first.
const REPORTS = [...annualReports].reverse();

function YearMarker() {
	return (
		<span aria-hidden className="bg-brand relative block size-2 rounded-full">
			<span className="absolute top-1/2 left-1/2 size-1 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white" />
		</span>
	);
}

/**
 * The scroll position is the state: `active` is read back off it. Horizontal though the rail is vertical,
 * because a vertical track would claim the page's own scroll gesture on a phone.
 */
export function Reports() {
	const { t } = useLingui();
	const { language } = useTranslation();
	// The newest, not the hand-off's 2023, which would walk back a year with every new report.
	const [active, setActive] = useState(0);
	const track = useRef<HTMLUListElement>(null);
	const last = REPORTS.length - 1;

	// `behavior` unset defers to the carousel's reduced-motion-aware `scroll-behavior`.
	const showYear = (index: number) => {
		const el = track.current;
		if (el) el.scrollTo({ left: index * carouselStep(el) });
	};

	const onScroll = () => {
		const el = track.current;
		if (!el) return;
		const step = carouselStep(el);
		if (step > 0) setActive(Math.min(last, Math.max(0, Math.round(el.scrollLeft / step))));
	};

	return (
		<section className="relative overflow-hidden bg-white">
			<DiamondGrid
				color="var(--color-brand)"
				opacity={0.95}
				masks={[diamondGlows({ x: '-10px', y: 'calc(100% - 22px)' })]}
			/>

			<div className="relative mx-auto flex max-w-[1440px] flex-col gap-10 px-6 py-16 md:py-20 xl:h-[748px] xl:gap-0 xl:px-0 xl:py-0">
				<div className="flex w-full max-w-[513px] flex-col gap-4 xl:absolute xl:top-[183px] xl:left-[93px]">
					<h2 className="text-ink text-4xl leading-[1.3] font-medium tracking-[-0.02em] md:text-[2.5rem]">
						{t`Our impact, year by year`}
					</h2>
					<p className="text-ink max-w-[496px] text-lg leading-[1.5] tracking-[-0.02em] md:text-xl">
						{t`We invite you to consult our annual corporate reports for the detail of the social impact achieved.`}
					</p>
				</div>

				<div className="flex items-center gap-4 md:gap-8 xl:contents">
					{/* A slide must be the frame's exact width for the snap points to land on the card edges. */}
					<Carousel
						ref={track}
						onScroll={onScroll}
						className="bg-sand carousel-no-buttons relative aspect-[529/383] min-w-0 flex-1 gap-0 rounded-3xl px-0 select-none xl:absolute xl:top-[183px] xl:left-[707px] xl:aspect-auto xl:h-[383px] xl:w-[529px] xl:flex-none"
					>
						{REPORTS.map((report, i) => (
							<CarouselItem
								key={report.year}
								// On the slide too: the overhanging illustration otherwise leaks into the next card.
								className="relative h-full w-full overflow-hidden"
								aria-hidden={i !== active}
							>
								{/* Provisional: 2023's drawing stands in for every year until the rest arrive. Decorative, no msgid. */}
								<HighFive
									active={i === active}
									className="pointer-events-none absolute top-[9.4%] left-[35.2%] h-[97.4%] w-[78.6%]"
								/>

								{/* The export's #F86A00 is a shade off `brand-strong` (#F66900). */}
								<p className="absolute top-[8.6%] left-[8.3%] w-[33.1%] text-right text-5xl leading-[1.3] font-medium text-[#F86A00] tabular-nums md:text-[4.5rem]">
									{report.year}
								</p>

								{/* No click guard: a native scroll is not a click. */}
								<a
									href={annualReportUrl(report.links, language)}
									target="_blank"
									rel="noopener noreferrer"
									tabIndex={i === active ? undefined : -1}
									className="text-ink absolute top-[82.5%] left-[9.1%] flex items-center gap-2 text-[0.9375rem] leading-[1.3] font-medium tracking-[-0.02em] transition-opacity hover:opacity-70"
								>
									{t`Download`}
									<Download className="size-4" />
								</a>
							</CarouselItem>
						))}
					</Carousel>

					{/* A grid, so the marker lands in the active year's row with no measuring. */}
					<div
						className="grid shrink-0 gap-x-[6px] gap-y-2 xl:absolute xl:top-[266px] xl:left-[1302px]"
						style={{ gridTemplateColumns: 'auto auto', gridTemplateRows: `repeat(${REPORTS.length}, auto)` }}
					>
						<span
							aria-hidden
							style={{ gridColumn: 1, gridRow: '1 / -1' }}
							// The export gives no colour; 20% matches the inactive years.
							className="bg-ink/20 my-2 w-px justify-self-center"
						/>
						<span
							aria-hidden
							style={{ gridColumn: 1, gridRow: active + 1 }}
							className="self-center justify-self-center"
						>
							<YearMarker />
						</span>
						{REPORTS.map((report, i) => (
							<button
								key={report.year}
								type="button"
								onClick={() => showYear(i)}
								aria-current={i === active ? 'true' : undefined}
								style={{ gridColumn: 2, gridRow: i + 1 }}
								className={cn(
									'text-ink cursor-pointer text-base leading-[1.5] font-medium tracking-[-0.02em] transition-opacity',
									i === active ? 'opacity-100' : 'opacity-20 hover:opacity-50',
								)}
							>
								{report.year}
							</button>
						))}
					</div>
				</div>

				<p className="text-ink/70 w-full max-w-[533px] text-sm leading-[1.5] tracking-[-0.02em] xl:absolute xl:top-[544px] xl:left-[93px]">
					{t`*Financial reports audited annually by AUDRIA Auditoría y Consultoría, S.L.P.`}
				</p>
			</div>
		</section>
	);
}
